import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useBooleanFlagValue } from '@openfeature/react-sdk';

import HeaderReadOnly from './components/headerReadOnly';
import SymptomsReadOnly from './components/symptomsReadOnly';
import InterventionsReadOnly from './components/interventionsReadOnly';
import GoalsProgress from '../goalsProgress/GoalsProgress';
import SessionNarrative from '../narrative/SessionNarrative';
import { FORTA_BLUE, FORTA_ERROR } from '../../../../../utils/colors';
import { getSessionSignatureStatusAndWarnings } from '../../utils';
import ClearSignatureIcon from '../../../../../components/svgs/ClearSignatureIcon';
import TrashIcon from '../../../../../components/svgs/TrashIcon3';
import { BTSessionStatus, Roles } from '../../../../../constants';
import { areUserRolesIn } from '../../../../../helpers';
import { FHButton, FHConfirmDialog } from '../../../../../components/base';
import { SESSION_NOTE_VERSIONS } from '../../constants';
import { FeatureFlags } from '../../../../../constants/featureFlags';
import {
  getSignatureStatusColor,
  shouldShowBackdateForm,
  shouldShowDeleteButton,
  shouldShowMakeNoteEditableButton,
} from './utils/general';
import BackdateForm from './components/backdateForm';
import {
  useBackdateSession,
  useDeleteSession,
  useDownloadSessionNote,
  useMakeSessionEditable,
} from './hooks';

import type { RootState } from '../../../../../store';

function SessionReadOnlyView() {
  const { t } = useTranslation('sessionNotes');
  const sessionNotesV2Enabled = useBooleanFlagValue(FeatureFlags.SESSION_NOTES_V2_ENABLED, false);
  const { current: session } = useSelector((state: RootState) => state.sessions);
  const { user } = useSelector((state: RootState) => state.auth);
  const useNewUi = sessionNotesV2Enabled && session?.note_version === SESSION_NOTE_VERSIONS.V2;

  const sessionSignAndWarningStatus = getSessionSignatureStatusAndWarnings(t, session);

  const showButtonToMakeNoteEditable = useMemo(
    () => shouldShowMakeNoteEditableButton(user?.roles ?? [], session),
    [session, user],
  );
  const showDeleteButton = useMemo(
    () => shouldShowDeleteButton(user?.roles ?? [], user?.email ?? '', session),
    [session, user],
  );
  const showBackdateForm = useMemo(
    () => shouldShowBackdateForm(user?.roles ?? [], session),
    [session, user],
  );

  const {
    showConfirmBackdateDialog,
    setShowConfirmBackdateDialog,
    requestBackdate,
    handleBackdateSession,
  } = useBackdateSession(session?.id);

  const { openConfirmDownload, setOpenConfirmDownload, handleDownloadSessionNote } =
    useDownloadSessionNote(session?.id);

  const {
    showConfirmMakeNoteEditableDialog,
    setShowConfirmMakeNoteEditableDialog,
    handleMakeSessionEditable,
  } = useMakeSessionEditable(session?.id);

  const { showConfirmDeleteDialog, setShowConfirmDeleteDialog, handleDeleteSession } =
    useDeleteSession(session?.id);

  if (!session) return null;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography
          textAlign="left"
          fontWeight={700}
          fontSize={21}
          sx={{ flexGrow: 1, mb: 2, mt: 1 }}
        ></Typography>
        <Box sx={{ display: 'flex', mb: 3 }}>
          <Box sx={{ display: 'flex' }}>
            <Box
              sx={{
                backgroundColor: getSignatureStatusColor(
                  !!sessionSignAndWarningStatus?.showParentSignatureWarning,
                  !!sessionSignAndWarningStatus?.isSigned,
                ),
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                zIndex: 100,
                pt: 0.5,
                pl: 0.5,
              }}
            >
              <ClearSignatureIcon width={30} height={40} />
            </Box>
            <Typography
              fontSize={16}
              sx={{
                ml: -4,
                border: `2px solid ${getSignatureStatusColor(
                  !!sessionSignAndWarningStatus?.showParentSignatureWarning,
                  !!sessionSignAndWarningStatus?.isSigned,
                )}`,
                pl: 5,
                pt: 0.7,
                pr: 1,
                height: 40,
                fontWeight: 700,
                borderRadius: '8px',
                backgroundColor: FORTA_BLUE.light3,
              }}
              fontWeight={sessionSignAndWarningStatus?.showParentSignatureWarning ? 700 : 400}
              color={
                sessionSignAndWarningStatus?.showParentSignatureWarning
                  ? FORTA_ERROR
                  : FORTA_BLUE.primary
              }
            >
              {sessionSignAndWarningStatus?.signatory ?? ''}
            </Typography>
          </Box>
          {showDeleteButton && (
            <Box>
              <IconButton
                sx={{
                  p: 1,
                  ml: { xs: 1, sm: 2 },
                  '&:hover': {
                    opacity: 1,
                  },
                  borderRadius: '8px',
                  border: `1px solid ${FORTA_ERROR}`,
                }}
                data-testid="deleteSessionButtonAdmin"
                onClick={() => setShowConfirmDeleteDialog(true)}
              >
                <TrashIcon color={FORTA_ERROR} width={18} height={18} />
                <Typography color={FORTA_ERROR} ml={1}>
                  Delete
                </Typography>
              </IconButton>
            </Box>
          )}
          {showButtonToMakeNoteEditable && (
            <Box display="flex" ml={2}>
              <FHButton onClick={() => setShowConfirmMakeNoteEditableDialog(true)}>Edit</FHButton>
            </Box>
          )}
          <Box>
            {session.status !== BTSessionStatus.Started &&
              session.status !== BTSessionStatus.AwaitingGuardianSignature &&
              areUserRolesIn(user?.roles, [
                Roles.Admin,
                Roles.QaSpecialist,
                Roles.ClientSuccessManager,
                Roles.Biller,
                Roles.InvoiceCoordinator,
                Roles.Bcba,
                Roles.AssistantAssessor,
              ]) && (
                <Box display="flex" ml={2} justifyContent="flex-end">
                  <FHButton
                    data-testid="downloadSessionNotes"
                    sx={{ width: '150px' }}
                    onClick={() => setOpenConfirmDownload(true)}
                  >
                    Download
                  </FHButton>
                </Box>
              )}
          </Box>
        </Box>
      </Box>
      {showBackdateForm && <BackdateForm session={session} onBackdate={requestBackdate} />}
      <HeaderReadOnly />
      <SymptomsReadOnly signsAndSymptomsObserved={session?.signs_and_symptoms_observed} />
      <InterventionsReadOnly
        evidenceBasedInterventionsUsed={session?.evidence_based_interventions_used}
      />
      <GoalsProgress
        isEditable={false}
        values={session}
        showErrors={false}
        updateFieldValue={() => {}}
        setGoalsPracticedList={() => {}}
        useNewUi={useNewUi}
      />
      <SessionNarrative
        isEditable={false}
        session={session}
        currentNarrative={session?.narrative}
        minimumLength={180}
        onChange={() => {}}
        showError={false}
        useNewUi={useNewUi}
      />
      <FHConfirmDialog
        open={showConfirmDeleteDialog}
        title={'Delete session'}
        content={'Are you sure you want to delete this session? This action cannot be undone.'}
        submittext={'Yes, delete'}
        canceltext={'Cancel'}
        submitBtnTestId={'deleteBtn'}
        cancelBtnTestId={'cancelBtn'}
        onClose={() => setShowConfirmDeleteDialog(false)}
        onSubmit={handleDeleteSession}
      />
      <FHConfirmDialog
        open={showConfirmMakeNoteEditableDialog}
        title={'Make editable'}
        content={'Are you sure you want to make this session note editable?'}
        submittext={'Yes'}
        canceltext={'Cancel'}
        onClose={() => setShowConfirmMakeNoteEditableDialog(false)}
        onSubmit={handleMakeSessionEditable}
      />
      <FHConfirmDialog
        open={openConfirmDownload}
        content={'Are you sure you want to download this session note?'}
        submittext={'Download'}
        canceltext={'Back to session note'}
        onClose={() => setOpenConfirmDownload(false)}
        onSubmit={() => {
          handleDownloadSessionNote();
        }}
      />
      <FHConfirmDialog
        open={showConfirmBackdateDialog}
        title={'Change'}
        content={'Are you sure you want to change the start date of this session note?'}
        submittext={'Yes'}
        canceltext={'Cancel'}
        onClose={() => setShowConfirmBackdateDialog(false)}
        onSubmit={handleBackdateSession}
      />
    </Box>
  );
}

export default SessionReadOnlyView;
